<script setup lang="ts">
import type { Cart } from '@/js/types/cart.ts';
import { reachGoal } from '@/js/goal.ts';
import { getLocaleValue } from '@/js/helper/locale.ts';
import { classname } from '@/js/utils.ts';
import BaseButton from '@/js/vue/base/base-button.vue';
import CartItem from '@/js/vue/cart/cart-item.vue';
import CartTotals from '@/js/vue/cart/cart-totals.vue';
import { useCartTotal } from '@/js/vue/composable/cart-total.ts';
import { watch } from 'vue';

const props = defineProps<{
  cart: Cart
  checkoutUrl: string
}>();

const cn = classname('cart');
const { setCartTotal, products, cartTotalSum, deleteCartItem, updateCartItem } = useCartTotal();
watch(() => props.cart, (cart) => {
  setCartTotal(cart);
}, {
  immediate: true,
});

function handleClick() {
  reachGoal('order-success');
}
</script>

<template>
  <div v-if="products.length" :class="cn('inner')">
    <div :class="cn('products')">
      <CartItem
        v-for="product in products"
        :key="product.id"
        :quantity="product.quantity"
        :image="product.productImage.url ?? ''"
        :price="product.price"
        :name="product.productName"
        :url="product.productUrl"
        @change-quantity="updateCartItem(product.id, $event)"
        @delete="deleteCartItem(product.id)"
      />
    </div>
    <CartTotals :class="cn('totals')" :total-sum="cartTotalSum">
      <BaseButton :to="checkoutUrl" theme="primary" @click="handleClick">
        {{ getLocaleValue('place_an_order') }}
      </BaseButton>
    </CartTotals>
  </div>
  <div v-else :class="cn('heading-container')">
    <div :class="cn('heading')">
      {{ getLocaleValue('empty_cart') }}
    </div>
    <a
      href="/" class="base-button base-button_type_primary base-button_size_small"
      :class="cn('button')"
    >
      {{ getLocaleValue('page_compare_continue_button') }}
    </a>
  </div>
</template>
